import React from "react"
import Loadable from "react-loadable"
import {
  LoadingComponent,
  withPageMetadata,
  metadata,
} from "@pga/pga-component-library"
import { PrivateRoute } from "../lib/auth.js"
import { Route, Switch, Redirect } from "react-router-dom"
import CheckEducationStatus from "./CheckEducationStatus.js"
import WrappedRouter from "./WrappedRouter.js"
import { defaultRoutes, educationRoutes } from "./RouteConfig.js"
import RouteChangeHandler from "../lib/Segment/RouteChangeHandler.js"
import { flags, useAllFeatureFlags } from "../lib/feature-flags"
import PtmApplicationsRedirect from "../components/Applications/PtmApplicationsRedirect"
import {
  MEMBER_LEGACY_APPLICATION_PATHS,
  PTM_LEGACY_APPLICATION_PATHS,
  isMemberLegacyApplicationPath,
  shouldSuppressPtmLegacyApplicationRoute,
} from "../lib/ptmApplicationsRedirect"

export const loading = LoadingComponent

const AsyncLoadable = loader =>
  Loadable({
    loader,
    loading,
    delay: 300,
  })

const NotFound = withPageMetadata(metadata("NOT FOUND"))(
  AsyncLoadable(() => import("../components/NotFound/index.js")),
)

export const isRouteEnabled =
  featureFlags =>
  ({ featureFlag }) =>
    !featureFlag || featureFlags?.[featureFlag]

export const Router = () => {
  const featureFlags = useAllFeatureFlags()
  // FeatureFlagProvider renders a loader until its first evaluation resolves,
  // so the router never mounts with undefined flags. A missing flag or a Flagr
  // failure (empty flags) falls back to the legacy routes.
  const memberApplicationsRedirect =
    featureFlags?.[flags.FLAG_FEAT_APPLICATIONS_MEMBERSHIP] === true

  return (
    <>
      <RouteChangeHandler />
      <Switch>
        <Redirect from="/dashboard" to="/" />
        {PTM_LEGACY_APPLICATION_PATHS.map(path => (
          <PrivateRoute
            key={path}
            exact
            path={path}
            component={PtmApplicationsRedirect}
          />
        ))}
        {/* TODO(cleanup): applications-membership flag branch. Remove the flag
            check and the defaultRoutes filter below when the redirect becomes
            unconditional. */}
        {memberApplicationsRedirect &&
          MEMBER_LEGACY_APPLICATION_PATHS.map(path => (
            <PrivateRoute
              key={path}
              exact
              path={path}
              component={PtmApplicationsRedirect}
            />
          ))}
        {defaultRoutes
          .filter(isRouteEnabled(featureFlags))
          .filter(route => !shouldSuppressPtmLegacyApplicationRoute(route.path))
          .filter(
            route =>
              !(
                memberApplicationsRedirect &&
                isMemberLegacyApplicationPath(route.path)
              ),
          )
          .map(({ exact, path, secured, component }) =>
            secured ? (
              <PrivateRoute
                key={path}
                exact={exact}
                path={path}
                component={component}
              />
            ) : (
              <Route
                key={path}
                exact={exact}
                path={path}
                component={component}
              />
            ),
          )}
        {educationRoutes.map(
          ({ checkEducationStatus: rules, ...routeProps }, idx) =>
            Array.isArray(rules) && rules.length > 0 ? (
              <CheckEducationStatus key={idx} rules={rules} {...routeProps} />
            ) : (
              <Route key={idx} {...routeProps} />
            ),
        )}
        <Route component={NotFound} />
      </Switch>
    </>
  )
}

export default WrappedRouter
